/**
 * What a carer reads before the app asks them for anything.
 *
 * The first slide is the welcome screen's own words, because that is what this
 * screen now is: the way in and the story arrived together rather than one
 * behind the other.
 *
 * The three after it are a first pass, and the one part of this that is a copy
 * decision rather than a layout one. The claims are deliberately small: nothing
 * here promises care we have not found or money nobody has checked for.
 */
type Slide = {
  id: string;
  title: string;
  body: string;
  /** The illustration. Absent while it is still a slot. */
  image?: string;
};

export const PRE_SIGN_UP = {
  getStarted: 'Get started',
  /** On the last screen, where there is nothing left to get started with. */
  createAccount: 'Create account',
  signIn: 'Sign in',
  /**
   * The chat opening: the same chat the intake is built on, asking the one
   * question that would route somebody, and answering the first of them.
   *
   * It answers as far as the words go and no further. None of these four lead
   * to a screen yet, and what this is here to find out is whether being asked
   * beats being told.
   */
  chat: {
    /** Said first, on its own, the way a person opens with hello. */
    greeting: 'Welcome to KareHero',
    /**
     * Then what we are and what is being asked, in one message: the welcome
     * screen's own line, and the question it used to take a tap to reach.
     */
    intro:
      'We are real care experts at your fingertips to help you find, understand and fund the right care. What can we help you with?',
    question: 'What can we help you with?',
    options: [
      { id: 'what', label: 'What is KareHero?' },
      { id: 'expert', label: 'Who are your care experts?' },
      { id: 'entitled', label: 'Can you help me check what I am entitled to?' },
      { id: 'finding', label: 'Can you help me find care?' },
    ],
    /** Asked after every answer, so it is one line rather than one per answer. */
    followUp: 'Is there anything else you want to know?',
    /**
     * What comes back when one is tapped, keyed by the answer.
     *
     * All four are written, so every answer answers. A fifth would work the
     * same way: an id in `options` and a reply here.
     */
    replies: {
      what: {
        lead: 'KareHero is a care concierge service. Real care experts you can talk to, who do the legwork with you:',
        list: [
          'Working out what care is needed',
          'Finding it, and understanding what it costs',
          'Claiming the money you are owed',
        ],
      },
      expert: {
        lead: 'KareHero has real people who do this every day. They know how the system works and what to ask for. They can help you:',
        list: [
          'Work out what care is needed',
          'Find and compare providers near you',
          'Understand what it costs and who pays for it',
        ],
      },
      finding: {
        lead: 'Yes. You tell us what is needed and where, and we come back with a shortlist rather than a directory:',
        list: [
          'Providers who have space, not just ones who exist',
          'What they charge, in writing',
          'We do the ringing round and the chasing',
        ],
      },
      entitled: {
        lead: 'Yes. We go through what you and the person you care for could claim, then fill the forms in with you. That is usually:',
        list: [
          'Attendance Allowance, the one most people miss',
          'Carer’s Allowance, if you are the one looking after them',
          'What the council will and will not pay towards care',
        ],
      },
    } as Record<string, { lead: string; list: string[] } | undefined>,
  },
  slides: [
    {
      id: 'welcome',
      title: 'Welcome to KareHero',
      body: 'Real care experts at your fingertips to help you find, fund and understand the right care.',
      // A real one. The rest are still slots: a drawing each is nobody's
      // decision yet, and a marked slot says so where a stand-in would not.
      image: '/pre-sign-up-welcome.jpg',
    },
    {
      id: 'experts',
      title: 'Real care experts',
      body: 'People who do this every day. They know how the system works and what to ask for.',
    },
    {
      id: 'money',
      title: 'The money you can claim',
      body: 'We check what you are entitled to, then fill the forms in with you.',
    },
    {
      id: 'care',
      title: 'Care found for you',
      body: 'We shortlist providers near you and chase them for answers, so you are not ringing round.',
    },
  ],
};
